import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/api/http';
import { tokenStore } from '@/lib/auth';
import { SPECIALTIES, APPROACHES, TARGET_AUDIENCE, LANGUAGES } from '@/lib/specialties';
import { BRAZILIAN_STATES } from '@/lib/brazilian-states';
import { CityAutocomplete } from '@/components/public/city-autocomplete';
import { TermsAcceptanceBlock } from '@/components/shared/terms-acceptance';

const CRP_REGEX = /^\d{2}\/\d{4,6}$/;

export const Route = createFileRoute('/onboarding/therapist')({
  beforeLoad: () => {
    if (!tokenStore.getAccess()) throw redirect({ to: '/login' });
  },
  component: TherapistOnboardingPage,
});

const schema = z.object({
  personType: z.enum(['PF', 'PJ']),
  cnpj: z.string().optional(),
  razaoSocial: z.string().optional(),
  taxRegime: z.enum(['MEI', 'SIMPLES', 'LUCRO_PRESUMIDO', 'LUCRO_REAL']).optional(),
  professionalCategory: z.enum(['PSYCHOLOGIST', 'THERAPIST']),
  crpNumber: z.string().optional(),
  crpRegion: z.string().optional(),
  therapyModality: z.string().optional(),
  bio: z.string().min(50, 'Mínimo 50 caracteres').max(2000),
  specialties: z.array(z.string()).min(1, 'Escolha ao menos 1'),
  approaches: z.array(z.string()).min(1),
  targetAudience: z.array(z.string()).min(1),
  languages: z.array(z.string()).min(1, 'Selecione ao menos um idioma'),
  sessionPrice: z.coerce.number().min(10, 'Valor mínimo: R$ 10,00'),
  sessionDuration: z.coerce.number().min(30).max(180),
  modality: z.enum(['ONLINE', 'PRESENCIAL', 'HYBRID']),
  city: z.string().optional(),
  state: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.professionalCategory === 'PSYCHOLOGIST') {
    if (!data.crpNumber?.trim() || !CRP_REGEX.test(data.crpNumber.trim())) {
      ctx.addIssue({ code: 'custom', path: ['crpNumber'], message: 'Formato: DD/NNNNNN (ex: 06/123456)' });
    }
    if (!data.crpRegion?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['crpRegion'], message: 'Selecione a UF de registro' });
    }
  } else if (data.professionalCategory === 'THERAPIST') {
    if (!data.therapyModality?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['therapyModality'], message: 'Selecione a modalidade' });
    }
  }
});
type FormData = z.infer<typeof schema>;

function TherapistOnboardingPage() {
  const navigate = useNavigate();
  // Step 0: escolha de categoria regulatória (doc §2/§3.1) — só libera o
  // restante do form depois de escolher Psicólogo ou Terapeuta.
  const [category, setCategory] = useState<'PSYCHOLOGIST' | 'THERAPIST' | null>(null);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      personType: 'PJ',
      modality: 'ONLINE',
      sessionDuration: 50,
      specialties: [],
      approaches: [],
      targetAudience: [],
      languages: ['Português'],
    },
  });

  // Lista controlada de modalidades (§4.2) — fonte única no backend.
  const { data: modalitiesData } = useQuery({
    queryKey: ['therapy-modalities'],
    queryFn: () => api.get('legal/therapy-modalities').json<{ items: { slug: string; label: string }[] }>(),
    enabled: category === 'THERAPIST',
  });
  const modalities = modalitiesData?.items ?? [];

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async (body: FormData) => {
      // Aceite formal vai antes do onboarding. Backend bloqueia o
      // POST /therapists/onboarding se TERMS_NOT_ACCEPTED.
      await api.post('auth/accept-terms').json<any>();
      // Limpa campos de outra categoria pra não confundir validação
      const payload: any = { ...body };
      if (payload.professionalCategory === 'PSYCHOLOGIST') {
        delete payload.therapyModality;
      } else {
        delete payload.crpNumber;
        delete payload.crpRegion;
      }
      return api.post('therapists/onboarding', { json: payload }).json<any>();
    },
    onSuccess: () => navigate({ to: '/dashboard' }),
  });

  function handleFormSubmit(d: FormData) {
    if (!termsAccepted) {
      setTermsError('Você precisa aceitar os Termos para continuar');
      return;
    }
    setTermsError(null);
    submit.mutate(d);
  }

  // Step 0 — cards de escolha de categoria (doc §3.1).
  if (!category) {
    return (
      <div className="min-h-screen bg-[#FBF8F3] py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 text-center">
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">Como você atende?</h1>
            <p className="text-slate-500 mt-2">Esta escolha define como seu perfil será apresentado e quais campos serão exigidos.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => {
                setCategory('PSYCHOLOGIST');
                setValue('professionalCategory', 'PSYCHOLOGIST');
              }}
              className="text-left bg-white rounded-2xl border border-slate-100 shadow-sm p-6 hover:border-teal-300 hover:shadow-md transition"
            >
              <div className="size-12 rounded-xl bg-teal-100 flex items-center justify-center text-2xl mb-3">🩺</div>
              <h2 className="text-xl font-bold text-slate-800">Sou Psicólogo(a)</h2>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                Tenho registro ativo no Conselho Regional de Psicologia (CRP). Vou informar o
                número e a UF do meu registro para verificação.
              </p>
              <p className="text-xs text-slate-400 mt-3">
                Profissão regulamentada · Lei 4.119/62
              </p>
            </button>
            <button
              type="button"
              onClick={() => {
                setCategory('THERAPIST');
                setValue('professionalCategory', 'THERAPIST');
              }}
              className="text-left bg-white rounded-2xl border border-slate-100 shadow-sm p-6 hover:border-coral-300 hover:shadow-md transition"
            >
              <div className="size-12 rounded-xl bg-coral-100 flex items-center justify-center text-2xl mb-3">🌿</div>
              <h2 className="text-xl font-bold text-slate-800">Sou Terapeuta</h2>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                Atuo em outra modalidade (psicanálise, terapia holística, integrativa, floral,
                etc.). Vou escolher minha modalidade de uma lista.
              </p>
              <p className="text-xs text-slate-400 mt-3">
                Atividade de livre exercício · não regulamentada
              </p>
            </button>
          </div>
          <p className="text-center text-xs text-slate-400 mt-6 max-w-xl mx-auto">
            Você poderá alterar isso apenas com aprovação do admin. A escolha define como seu
            perfil aparece para os clientes e quais termos podem ser usados nos textos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF8F3] py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-coral-50 border border-coral-100 rounded-full px-4 py-1.5 text-coral-600 text-sm font-medium mb-4">
            Cadastro · {category === 'PSYCHOLOGIST' ? 'Psicólogo(a)' : 'Terapeuta'}
            <button type="button" onClick={() => setCategory(null)} className="ml-2 text-coral-500 hover:text-coral-700 underline">trocar</button>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-800">Configure seu perfil profissional</h1>
          <p className="text-slate-500 mt-2">Quanto mais completo, mais clientes você atrai.</p>

          <div className="mt-5 rounded-2xl bg-gradient-to-r from-amber-50 to-amber-100 border-2 border-amber-300 p-4 flex items-start gap-3">
            <div className="text-2xl shrink-0">🎁</div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-amber-900">Promoção de adesão ativa</h3>
              <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                Nos <strong>3 primeiros meses</strong> após concluir este cadastro, a plataforma
                <strong> não cobra comissão</strong> das suas sessões. Você recebe <strong>100% do
                valor</strong> que o cliente pagar. A contagem começa agora.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-6">
          <input type="hidden" {...register('professionalCategory')} value={category} />
          <Section title="Registro profissional">
            {category === 'PSYCHOLOGIST' ? (
              <>
                <div className="rounded-xl bg-teal-50 border border-teal-100 px-4 py-3 text-sm text-teal-800 mb-2">
                  Seu perfil só aparecerá publicamente após a verificação do CRP pela nossa equipe. Isso costuma levar até 2 dias úteis.
                </div>
                <div className="grid sm:grid-cols-[2fr_1fr] gap-4">
                  <Field label="Número do CRP" error={errors.crpNumber?.message as string | undefined}>
                    <input {...register('crpNumber')} placeholder="06/123456" className="input" />
                  </Field>
                  <Field label="UF" error={errors.crpRegion?.message as string | undefined}>
                    <select {...register('crpRegion')} className="input">
                      <option value="">UF…</option>
                      {BRAZILIAN_STATES.map((s) => <option key={s.uf} value={s.uf}>{s.uf}</option>)}
                    </select>
                  </Field>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 mb-2">
                  Sua modalidade é uma <strong>atividade de livre exercício</strong>, não regulamentada por conselho. Esta informação será exibida no seu perfil público para transparência ao cliente.
                </div>
                <Field label="Modalidade" error={errors.therapyModality?.message as string | undefined}>
                  <select {...register('therapyModality')} className="input">
                    <option value="">Selecione…</option>
                    {modalities.map((m) => <option key={m.slug} value={m.slug}>{m.label}</option>)}
                  </select>
                </Field>
              </>
            )}
          </Section>

          <Section title="Dados fiscais">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Tipo de pessoa">
                <select {...register('personType')} className="input">
                  <option value="PF">Pessoa Física</option>
                  <option value="PJ">Pessoa Jurídica</option>
                </select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="CNPJ (se PJ)">
                <input {...register('cnpj')} placeholder="00.000.000/0000-00" className="input" />
              </Field>
              <Field label="Regime tributário">
                <select {...register('taxRegime')} className="input">
                  <option value="">Não informado</option>
                  {/* Doc Contabilidade §4.2 — MEI é vedado a psicólogos */}
                  {category !== 'PSYCHOLOGIST' && <option value="MEI">MEI</option>}
                  <option value="SIMPLES">Simples Nacional</option>
                  <option value="LUCRO_PRESUMIDO">Lucro Presumido</option>
                  <option value="LUCRO_REAL">Lucro Real</option>
                </select>
              </Field>
            </div>
          </Section>

          <Section title="Sobre você">
            <Field label="Bio (mín. 50 caracteres)" error={errors.bio?.message}>
              <textarea {...register('bio')} rows={5} placeholder="Conte sobre sua experiência, especialização e abordagem..." className="input min-h-[140px] py-3" />
            </Field>
          </Section>

          <Section title="Especialidades, abordagens e público">
            <CheckGroup label="Especialidades (escolha as principais)" name="specialties" options={SPECIALTIES as readonly string[]} register={register} error={errors.specialties?.message as string | undefined} tone="coral" />
            <CheckGroup label="Abordagens" name="approaches" options={APPROACHES as readonly string[]} register={register} error={errors.approaches?.message as string | undefined} tone="teal" />
            <CheckGroup label="Público-alvo" name="targetAudience" options={TARGET_AUDIENCE as readonly string[]} register={register} error={errors.targetAudience?.message as string | undefined} tone="cream" />
            <CheckGroup label="Idiomas atendidos" name="languages" options={LANGUAGES as readonly string[]} register={register} error={errors.languages?.message as string | undefined} tone="teal" />
          </Section>

          <Section title="Sessões">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Preço (R$)" error={errors.sessionPrice?.message}>
                <input type="number" min={10} step={1} placeholder="mínimo R$ 10,00" {...register('sessionPrice')} className="input" />
              </Field>
              <Field label="Duração (min)" error={errors.sessionDuration?.message}>
                <input type="number" min={30} max={180} {...register('sessionDuration')} className="input" />
              </Field>
              <Field label="Modalidade">
                <select {...register('modality')} className="input">
                  <option value="ONLINE">Online</option>
                  <option value="PRESENCIAL">Presencial</option>
                  <option value="HYBRID">Online ou presencial</option>
                </select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Cidade">
                <CityAutocomplete state={watch('state')} registerProps={register('city')} className="input" />
              </Field>
              <Field label="Estado">
                <select {...register('state')} className="input">
                  <option value="">Selecione...</option>
                  {BRAZILIAN_STATES.map((s) => (
                    <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                  ))}
                </select>
              </Field>
            </div>
          </Section>

          <TermsAcceptanceBlock
            checked={termsAccepted}
            onChange={(v) => { setTermsAccepted(v); if (v) setTermsError(null); }}
            error={termsError}
          />

          {submit.isError && <p className="text-sm text-red-500 text-center">Algo deu errado. Verifique os campos.</p>}

          <button
            type="submit"
            disabled={submit.isPending || !termsAccepted}
            className="w-full bg-coral-400 hover:bg-coral-500 text-white rounded-full h-12 font-bold transition shadow disabled:opacity-50"
          >
            {submit.isPending ? 'Salvando...' : 'Concluir cadastro →'}
          </button>
        </form>
      </div>
      <style>{`.input{width:100%;background:white;border:1px solid rgb(226 232 240);border-radius:12px;height:44px;padding:0 12px;outline:none}.input:focus{box-shadow:0 0 0 2px rgb(112 208 192)}`}</style>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-4 pt-4 border-t border-slate-100 first:border-t-0 first:pt-0">
      <legend className="text-sm font-bold text-slate-700 uppercase tracking-wide">{title}</legend>
      {children}
    </fieldset>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </label>
  );
}

function CheckGroup({ label, name, options, register, error, tone }: {
  label: string; name: keyof FormData; options: readonly string[]; register: any; error?: string;
  tone: 'teal' | 'coral' | 'cream';
}) {
  const colors = {
    teal: 'peer-checked:bg-teal-100 peer-checked:text-teal-700 peer-checked:border-teal-300',
    coral: 'peer-checked:bg-coral-100 peer-checked:text-coral-700 peer-checked:border-coral-300',
    cream: 'peer-checked:bg-amber-100 peer-checked:text-amber-700 peer-checked:border-amber-300',
  }[tone];

  return (
    <div>
      <p className="text-sm font-medium text-slate-700 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <label key={opt} className="cursor-pointer">
            <input type="checkbox" value={opt} {...register(name)} className="peer hidden" />
            <span className={`text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 transition ${colors}`}>
              {opt}
            </span>
          </label>
        ))}
      </div>
      {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
    </div>
  );
}
